import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type {
  SingleBillInfoFormValues,
  SingleBillInfoFormData,
  SingleBillInfoFormHandlers,
} from '@ctct/application-layer';
import type { SingleBillInfoFormHooks } from '@ctct/state-client-layer';

import { SingleBillInfoForm } from './components/SingleBillInfoForm';

interface Props {
  data: SingleBillInfoFormData;
  handlers: SingleBillInfoFormHandlers;
  hooks: SingleBillInfoFormHooks;
}

export const FormWithinContext = ({ data, handlers, hooks }: Props) => {
  const { setValue, getValues, trigger, formState, clearErrors } = useFormContext<SingleBillInfoFormValues>();

  const zipcode = useWatch<SingleBillInfoFormValues>({ name: 'zipcode' }) as string | undefined;
  const watchedCountryCode = useWatch<SingleBillInfoFormValues>({ name: 'country' }) as string | undefined;
  const watchedCurrencyCode = useWatch<SingleBillInfoFormValues>({ name: 'currency' }) as string | undefined;
  const selectedBillingCountry = hooks.useSelectedBillingCountry();
  const selectedBillingCurrency = hooks.useSelectedBillingCurrency();
  const selectedCountryCode = selectedBillingCountry || watchedCountryCode;
  const pendingCurrencySyncRef = useRef<string | null>(null);
  const pendingCountrySyncRef = useRef<string | null>(null);

  useEffect(() => {
    if (zipcode) {
      handlers.onZipCodeChange(zipcode, { setValue, getValues });
    }
  }, [handlers, zipcode, getValues, setValue]);

  useEffect(() => {
    handlers.addTriggerValidation(() => trigger(undefined, { shouldFocus: true }));
  }, [handlers, trigger]);

  useEffect(() => {
    handlers.addGetFormValues(getValues);
  }, [handlers, getValues]);

  useEffect(() => {
    handlers.addSetFormValue(setValue);
  }, [handlers, setValue]);

  useEffect(() => {
    handlers.addClearErrors(clearErrors);
  }, [handlers, clearErrors]);

  useEffect(() => {
    handlers.onIsDirtyChange(formState.isDirty);
  }, [handlers, formState.isDirty]);

  useEffect(() => {
    if (selectedBillingCountry && getValues('country') !== selectedBillingCountry) {
      pendingCountrySyncRef.current = selectedBillingCountry;
      setValue('country', selectedBillingCountry, { shouldDirty: true });
    }
  }, [getValues, selectedBillingCountry, setValue]);

  useEffect(() => {
    if (selectedBillingCurrency && getValues('currency') !== selectedBillingCurrency) {
      pendingCurrencySyncRef.current = selectedBillingCurrency;
      setValue('currency', selectedBillingCurrency, { shouldDirty: true });
    }
  }, [getValues, selectedBillingCurrency, setValue]);

  useEffect(() => {
    if (!watchedCurrencyCode) return;

    if (pendingCurrencySyncRef.current) {
      if (watchedCurrencyCode === pendingCurrencySyncRef.current) {
        pendingCurrencySyncRef.current = null;
      }

      return;
    }

    if (watchedCurrencyCode !== selectedBillingCurrency) {
      const didChangeCurrency = handlers.onCurrencyChange?.(watchedCurrencyCode);

      if (didChangeCurrency === false && selectedBillingCurrency) {
        pendingCurrencySyncRef.current = selectedBillingCurrency;
        setValue('currency', selectedBillingCurrency, { shouldDirty: true });
      }
    }
  }, [handlers, selectedBillingCurrency, setValue, watchedCurrencyCode]);

  useEffect(() => {
    if (!watchedCountryCode) return;

    if (pendingCountrySyncRef.current) {
      if (watchedCountryCode === pendingCountrySyncRef.current) {
        pendingCountrySyncRef.current = null;
      }

      return;
    }

    if (watchedCountryCode !== selectedBillingCountry) {
      const didChange = handlers.onBillingCountryChange?.(watchedCountryCode);

      if (didChange === false && selectedBillingCountry) {
        pendingCountrySyncRef.current = selectedBillingCountry;
        setValue('country', selectedBillingCountry, { shouldDirty: true });
      }
    }
  }, [handlers, selectedBillingCountry, setValue, watchedCountryCode]);

  useEffect(() => {
    if (selectedCountryCode) {
      handlers.onCountryChange(selectedCountryCode, { setValue, defaultValues: data.getDefaultValues() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode]);

  const copy = data.getSingleBillInfoFormWidgetCopy();

  return (
    <SingleBillInfoForm
      isEditing={data.isEditing()}
      copy={copy}
      countriesOptions={data.getCountries()}
      disableCountry={data.shouldDisableCountry()}
      dayOptions={data.getDayOfMonthOptions()}
      currrencyOptions={data.getCurrencyOptions()}
      isShowingStates={data.isShowingStates(selectedCountryCode)}
      stateLabel={data.getStateLabel(selectedCountryCode)}
      states={data.getStates(selectedCountryCode)}
      statePlaceholder={data.getStatePlaceholder(selectedCountryCode)}
      // vat
      showTaxNumber={data.isShowingTaxNumber(selectedCountryCode)}
      taxDisclaimer={data.getTaxDisclaimer(selectedCountryCode)}
      taxLabel={data.getTaxLabel(selectedCountryCode)}
    />
  );
};
